import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SPANISH_PROVINCES, LEAD_SOURCE_OPTIONS } from "@/lib/constants";

const emptyPatient = {
  name: "",
  surname: "",
  dni: "",
  dob: "",
  gender: "",
  phone: "",
  phone_alt: "",
  phone_alt_relation: "",
  email: "",
  address: "",
  city: "",
  postal_code: "",
  province: "",
  center_id: "",
  lead_source: "",
  referring_doctor: "",
  notes: "",
  audiologist_ids: [],
  primary_audiologist_id: "",
  disability: false,
  disability_percent: "",
};

const LEAD_SOURCES = LEAD_SOURCE_OPTIONS;

export default function Patients() {
  const { user, centers } = useAuth();
  const [patients, setPatients] = useState([]);
  const [audiologists, setAudiologists] = useState([]);
  const [search, setSearch] = useState("");
  const [centerFilter, setCenterFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPatient);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = { status: statusFilter };
    if (search) params.search = search;
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    const [{ data }, { data: users }] = await Promise.all([
      api.get("/patients", { params }),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setPatients(data);
    setAudiologists(users.filter((u) => ["audioprotesista", "admin", "gerente"].includes(u.role) && u.active !== false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.center_id) payload.center_id = user?.center_id || centers[0]?.id;
      payload.disability_percent = payload.disability && payload.disability_percent
        ? parseInt(payload.disability_percent) || null
        : null;
      await api.post("/patients", payload);
      toast.success("Paciente creado");
      setOpen(false);
      setForm(emptyPatient);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.duplicates) {
        const dup = detail.duplicates[0];
        toast.error(
          `Paciente duplicado: ${dup.name} (coincidencia por ${dup.by}). No se ha creado.`,
          { duration: 6000 },
        );
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Pacientes
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Ficha de pacientes
          </h1>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-patient-btn"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl bg-white max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nuevo paciente</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4 overflow-y-auto pr-2 scrollbar-thin flex-1">
              <div>
                <Label>Nombre *</Label>
                <Input
                  data-testid="patient-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Apellidos</Label>
                <Input
                  data-testid="patient-surname"
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                />
              </div>
              <div>
                <Label>DNI / NIE</Label>
                <Input
                  data-testid="patient-dni"
                  value={form.dni}
                  onChange={(e) => setForm({ ...form, dni: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha nacimiento</Label>
                <Input
                  type="date"
                  value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                />
              </div>
              <div>
                <Label>Género</Label>
                <Select
                  value={form.gender}
                  onValueChange={(v) => setForm({ ...form, gender: v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Masculino">Masculino</SelectItem>
                    <SelectItem value="Femenino">Femenino</SelectItem>
                    <SelectItem value="Otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Tel. adicional (familiar/amigo)</Label>
                <Input
                  data-testid="patient-phone-alt"
                  value={form.phone_alt}
                  onChange={(e) => setForm({ ...form, phone_alt: e.target.value })}
                  placeholder="Opcional"
                />
              </div>
              <div>
                <Label>Relación del contacto</Label>
                <Input
                  data-testid="patient-phone-alt-rel"
                  value={form.phone_alt_relation}
                  onChange={(e) => setForm({ ...form, phone_alt_relation: e.target.value })}
                  placeholder="Ej. hija, hijo, esposo…"
                />
              </div>
              <div className="md:col-span-2 xl:col-span-3">
                <Label>Audioprotesistas asignados</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-2 border rounded-md bg-[#FAF9F6]" data-testid="patient-audiologists">
                  {audiologists.length === 0 && (
                    <span className="text-xs text-[#6E7772]">Sin audioprotesistas en el sistema.</span>
                  )}
                  {audiologists.map((au) => {
                    const checked = form.audiologist_ids.includes(au.id);
                    const isPrimary = form.primary_audiologist_id === au.id;
                    return (
                      <label
                        key={au.id}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                          checked ? "bg-[#4A5D4E] text-white" : "bg-white border border-[#E5E3DB] text-[#4B5563]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          className="hidden"
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.audiologist_ids, au.id]
                              : form.audiologist_ids.filter((x) => x !== au.id);
                            setForm({
                              ...form,
                              audiologist_ids: next,
                              primary_audiologist_id: next.includes(form.primary_audiologist_id)
                                ? form.primary_audiologist_id
                                : (next[0] || ""),
                            });
                          }}
                        />
                        {au.name}
                        {au.role !== "audioprotesista" && (
                          <span
                            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              checked ? "bg-white/20 text-white" : "bg-[#EAE6DF] text-[#6E7772]"
                            }`}
                            title={`Este usuario es ${au.role === "admin" ? "administrador" : "gerente"} y también ejerce como audioprotesista`}
                          >
                            {au.role === "admin" ? "Admin" : "Gerente"}
                          </span>
                        )}
                        {checked && (
                          <button
                            type="button"
                            className={`ml-1 text-[10px] px-1 rounded ${isPrimary ? "bg-white text-[#4A5D4E]" : "bg-[#D9D6CC] text-[#2A2F2C]"}`}
                            onClick={(e) => {
                              e.preventDefault();
                              setForm({ ...form, primary_audiologist_id: au.id });
                            }}
                            data-testid={`set-primary-${au.id}`}
                          >
                            {isPrimary ? "Principal" : "Hacer principal"}
                          </button>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Procedencia</Label>
                <Select
                  value={form.lead_source || "none"}
                  onValueChange={(v) => setForm({ ...form, lead_source: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="patient-lead-source"><SelectValue placeholder="¿Cómo nos conoció?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {LEAD_SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Centro *</Label>
                {["admin", "center_admin"].includes(user?.role) ? (
                  <Select
                    value={form.center_id}
                    onValueChange={(v) => setForm({ ...form, center_id: v })}
                  >
                    <SelectTrigger data-testid="patient-center">
                      <SelectValue placeholder="Selecciona centro" />
                    </SelectTrigger>
                    <SelectContent>
                      {centers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={centerName(user?.center_id)} disabled />
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              <div>
                <Label>Población</Label>
                <Input
                  data-testid="patient-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Ej. Castellón de la Plana"
                />
              </div>
              <div>
                <Label>Código postal</Label>
                <Input
                  data-testid="patient-postal-code"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                  placeholder="12005"
                  inputMode="numeric"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Provincia</Label>
                <Select
                  value={form.province || "none"}
                  onValueChange={(v) => setForm({ ...form, province: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="patient-province" className="bg-white">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">—</SelectItem>
                    {SPANISH_PROVINCES.map((prov) => (
                      <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Médico derivante (opcional)</Label>
                <Input
                  data-testid="patient-referring-doctor"
                  value={form.referring_doctor}
                  onChange={(e) => setForm({ ...form, referring_doctor: e.target.value })}
                  placeholder="Ej. Dr. Antonio López — Hospital General"
                />
              </div>
              <div className="md:col-span-2 xl:col-span-3 mt-2 pt-3 border-t border-[#E5E3DB]">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="patient-disability-label">
                  <input
                    type="checkbox"
                    data-testid="patient-disability"
                    checked={!!form.disability}
                    onChange={(e) => setForm({ ...form, disability: e.target.checked })}
                  />
                  <span className="font-medium text-[#2A2F2C]">
                    Tiene certificado de minusvalía / discapacidad
                  </span>
                  <span className="text-xs text-[#6E7772]">
                    (las facturas a este paciente aplicarán IVA reducido del 4% en todas las líneas)
                  </span>
                </label>
                {form.disability && (
                  <div className="mt-2 max-w-xs">
                    <Label className="text-xs">% de minusvalía (opcional)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={form.disability_percent}
                      onChange={(e) => setForm({ ...form, disability_percent: e.target.value })}
                      placeholder="Ej. 33"
                      data-testid="patient-disability-percent"
                    />
                  </div>
                )}
              </div>

              <div className="md:col-span-2 xl:col-span-3">
                <Label>Notas</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <DialogFooter className="md:col-span-2 xl:col-span-3 sticky bottom-0 bg-white pt-3 -mx-2 px-2 border-t border-[#E5E3DB]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-patient-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7772]" />
          <Input
            data-testid="patient-search"
            placeholder="Buscar por nombre, DNI, teléfono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <div className="w-full md:w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-white" data-testid="patient-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deceased">Fallecidos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {["admin", "center_admin"].includes(user?.role) && (
          <div className="w-full md:w-56">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E3DB]">
              <TableHead className="w-20">Nº</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>DNI</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Nacimiento</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead>Alta</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {patients.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#6E7772] py-12">
                  Sin pacientes. Crea el primero con &quot;Nuevo paciente&quot;.
                </TableCell>
              </TableRow>
            )}
            {patients.map((p) => (
              <TableRow
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                className="border-[#E5E3DB]"
              >
                <TableCell className="font-mono text-sm text-[#6E7772]">
                  #{String(p.patient_number).padStart(4, "0")}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {p.name} {p.surname}
                    {p.status === "deceased" && (
                      <Badge variant="outline" className="text-[10px] border-[#9CA3AF] text-[#6E7772]" data-testid={`status-deceased-${p.id}`}>
                        † Fallecido
                      </Badge>
                    )}
                    {p.status === "inactive" && (
                      <Badge variant="outline" className="text-[10px]">Inactivo</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{p.dni || "—"}</TableCell>
                <TableCell>{p.phone || "—"}</TableCell>
                <TableCell>
                  {p.dob ? (
                    formatDate(p.dob)
                  ) : (
                    <Badge className="bg-[#FBF3E4] text-[#7C5A2A] hover:bg-[#FBF3E4] text-[10px]" title="Sin fecha de nacimiento — complétala para avisos de cumpleaños" data-testid={`dob-missing-${p.id}`}>
                      Falta
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{centerName(p.center_id)}</TableCell>
                <TableCell>{formatDate(p.created_at)}</TableCell>
                <TableCell>
                  <Link
                    to={`/pacientes/${p.id}`}
                    className="text-[#4A5D4E] hover:text-[#3C4A3F] inline-flex items-center text-sm font-medium"
                    data-testid={`open-patient-${p.id}`}
                  >
                    Abrir <ChevronRight className="h-4 w-4 ml-0.5" />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
